import Contact from '../default/Contact'
import Project from '../default/Project'
import ProjectData from './ProjectData'
import FormData from './FormData'
import FormFollow from './FormFollow'
import Axios from 'axios'
import Config from '@/Config'
import FetcherHelper from '@/helpers/FetcherHelper'
import DBEntryLogs from '@/helpers/DBEntryLogs'
import { GeoJSON, convertMutliPolygonToFeatureCollection } from '@/helpers/GeoJSON'
import moment from 'moment'
import _ from 'lodash'
import Tool from '@/helpers/tool'

export default class FormCollectDataGW {
  constructor () {
    this.id = 0
    this.publicId = null
    this.projectOwner = new Contact()
    this.projectSupervisor = new Contact()
    this.project = new Project()
    this.projectData = new ProjectData()
    this.formData = new FormData()
    this.geom = '' // FeatureCollection in geoJSON format
    this.logs = new DBEntryLogs()
    this.extent = null
    this.followData = new FormFollow()
    this.deathline = null
  }

  static setPrototype (obj) {
    Object.setPrototypeOf(obj, FormCollectDataGW.prototype)
    Object.setPrototypeOf(obj.logs, DBEntryLogs.prototype)
    obj.logs.logs = _.orderBy(obj.logs.logs, ['value.date'], ['desc'])
    obj.geom = convertMutliPolygonToFeatureCollection(obj.geom)
    obj.extent = GeoJSON.readGeometry(JSON.parse(obj.extent))
    FormCollectDataGW.setFilterAggregateField(obj)
    return obj
  }

  static setFilterAggregateField (obj) {
    obj.filterAggregateField = {
      hasObservationPoint:
        obj.formData.observationPoint.isPiezo ||
        obj.formData.observationPoint.isWell ||
        obj.formData.observationPoint.isSources ||
        obj.formData.observationPoint.isOther,
      hasQuantData:
        obj.formData.gwLevel.isAuto ||
        obj.formData.gwLevel.isManual ||
        obj.formData.sourceFlow.isAuto ||
        obj.formData.sourceFlow.isManual,
      hasQualdata:
        obj.formData.inSituMeasure.isAuto ||
        obj.formData.inSituMeasure.isManual ||
        obj.formData.inSituMeasure.isDiagraphies ||
        obj.formData.sample.isChemical ||
        obj.formData.sample.isProtocol ||
        obj.formData.sample.isSamplingPoint,
      hasPlottingTest:
        obj.formData.plottingTest.format.isCsv ||
        obj.formData.plottingTest.format.isGeodata ||
        obj.formData.plottingTest.format.isText ||
        obj.formData.plottingTest.format.isOther,
      hasReport:
        obj.formData.report.isAppendice ||
        obj.formData.report.isMap ||
        obj.formData.report.isOther ||
        obj.formData.report.isPictures ||
        obj.formData.report.isProfil ||
        obj.formData.report.isReport
    }
    return obj
  }

  static get apiBaseUrl () {
    return `${Config.apiBaseUrl}/forms/collect-data-gw`
  }

  static list () {
    return new Promise((resolve, reject) => {
      Axios.get(FormCollectDataGW.apiBaseUrl, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(response.data.map(d => FormCollectDataGW.setPrototype(d)))
        }).catch(reject)
    })
  }

  static get (publicId) {
    return new Promise((resolve, reject) => {
      Axios.get(`${FormCollectDataGW.apiBaseUrl}/${publicId}`, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(FormCollectDataGW.setPrototype(response.data))
        }).catch(reject)
    })
  }

  static filter (forms, filterData) {
    let toReturn = forms
    let filterConditions = _.pickBy(filterData, (val) => { return val })

    toReturn = _.filter(forms, {
      filterAggregateField: filterConditions
    })

    return toReturn
  }

  static toCsv (forms) {
    let toReturn = []

    forms.map((form) => {
      toReturn.push({
        id: form.publicId,
        status: form.status,
        nomDuProjet: form.project.name,
        auteurDuDepot: form.projectOwner.companyName,
        maitreOuvrage: form.projectSupervisor.companyName,
        dateCreation: form.createDate,
        dernièreModificationFormulaire: form.lastUpdateDate,
        dateLimite: form.deathline === null ? '-' : moment(form.deathline).format('DD.MM.YYYY'),
        contientPointObservation: form.filterAggregateField.hasObservationPoint,
        contientDonneeQuant: form.filterAggregateField.hasQuantData,
        contientDonneeQual: form.filterAggregateField.hasQualdata,
        contientEssaiDeTracage: form.filterAggregateField.hasPlottingTest,
        contientDocument: form.filterAggregateField.hasReport,
        url: `https://strates-vs.crealp.ch/form/collect-data-gw/${form.publicId}`
      })
    })

    return Tool.toCsv(toReturn)
  }

  get createDate () {
    return this.logs.getLog('CREATE') === null ? '-' : moment(this.logs.getLog('CREATE').date).format('DD.MM.YYYY')
  }

  get lastUpdateDate () {
    let toReturn = this.logs.getLastLog('UPDATE')
    return toReturn === null ? '-' : moment(toReturn.date).format('DD.MM.YYYY')
  }

  get formattedDeathline () {
    return this.deathline === null ? '-' : moment(this.deathline).format('DD.MM.YYYY')
  }

  get nbDaysBeforeDeathline () {
    return this.deathline === null ? '-' : (moment().diff(this.deathline, 'days') * -1)
  }

  save () {
    return new Promise((resolve, reject) => {
      if (this.id === 0) {
        // Create
        Axios.post(FormCollectDataGW.apiBaseUrl, this, FetcherHelper.getHeaders())
          .then((response) => {
            resolve()
          }).catch(reject)
      } else {
        // update
        Axios.put(`${FormCollectDataGW.apiBaseUrl}/${this.id}`, this, FetcherHelper.getHeaders())
          .then((response) => {
            resolve()
          }).catch(reject)
      }
    })
  }

  delete () {
    return Axios.delete(`${FormCollectDataGW.apiBaseUrl}/${this.id}`, FetcherHelper.getHeaders())
  }

  addLog (log) {
    return new Promise((resolve, reject) => {
      Axios.post(`${FormCollectDataGW.apiBaseUrl}/${this.id}/log`, log, FetcherHelper.getHeaders())
        .then((response) => {
          this.logs = response.data
          this.logs.logs = _.orderBy(this.logs.logs, ['value.date'], ['desc'])
          Object.setPrototypeOf(this.logs, DBEntryLogs.prototype)
          resolve(this)
        }).catch(reject)
    })
  }

  changeStatus (status) {
    return new Promise((resolve, reject) => {
      Axios.patch(`${FormCollectDataGW.apiBaseUrl}/${this.id}/status`, {
        status: status
      }, FetcherHelper.getHeaders())
        .then((response) => {
          this.status = response.data.status
          this.logs = response.data.logs
          this.logs.logs = _.orderBy(this.logs.logs, ['value.date'], ['desc'])
          Object.setPrototypeOf(this.logs, DBEntryLogs.prototype)
          resolve(this)
        }).catch(reject)
    })
  }

  validateStep (step) {
    return new Promise((resolve, reject) => {
      Axios.patch(`${FormCollectDataGW.apiBaseUrl}/${this.id}/follow/validate-step/${step}`, this, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(FormCollectDataGW.setPrototype(response.data))
        }).catch(reject)
    })
  }

  isLate () {
    return this.status === 'waiting' && (moment().diff(this.deathline, 'days') * -1) < 7
  }

  sendReminder () {
    return new Promise((resolve, reject) => {
      Axios.patch(`${FormCollectDataGW.apiBaseUrl}/${this.id}/follow/reminder`, this, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(FormCollectDataGW.setPrototype(response.data))
        }).catch(reject)
    })
  }

  extendDeathline () {
    return new Promise((resolve, reject) => {
      Axios.patch(`${FormCollectDataGW.apiBaseUrl}/${this.id}/follow/extend-deathline`, this, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(FormCollectDataGW.setPrototype(response.data))
        }).catch(reject)
    })
  }

  getStoragePassword () {
    return new Promise((resolve, reject) => {
      Axios.get(`${FormCollectDataGW.apiBaseUrl}/${this.id}/follow/getStoragePwd`, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }
}
